export const getTxId = txOrId => {
  if (typeof txOrId === 'string') {
    return txOrId;
  }

  return txOrId?.id?.uuid || null;
};

export const STREAM_MESSAGE_NOTIFICATION_ID_PREFIX = 'stream-message:';
export const STREAM_MESSAGE_FALLBACK_HEADLINE = 'New Message';
export const STREAM_MESSAGE_FALLBACK_SNIPPET = 'You have a new message.';

const normalizeNotificationUserId = value => {
  const normalized = String(value || '').trim();
  return normalized || null;
};

const normalizeMessageNotificationText = value => {
  return String(value || '').trim();
};

const hasNonFallbackMessageHeadline = notification => {
  const headline = normalizeMessageNotificationText(notification?.headline);
  return !!headline && headline !== STREAM_MESSAGE_FALLBACK_HEADLINE;
};

const hasNonFallbackMessageSnippet = notification => {
  const snippet = normalizeMessageNotificationText(notification?.snippet);
  return !!snippet && snippet !== STREAM_MESSAGE_FALLBACK_SNIPPET;
};

const getMessageNotificationHydrationScore = notification => {
  if (notification?.type !== 'message.new') {
    return 0;
  }

  const actorName = normalizeMessageNotificationText(notification?.actorName);
  const listingName = normalizeMessageNotificationText(
    notification?.listingName || notification?.listingTitle
  );
  let score = 0;

  if (actorName) {
    score += 4;
  }

  if (hasNonFallbackMessageHeadline(notification)) {
    score += 2;
  }

  if (hasNonFallbackMessageSnippet(notification)) {
    score += 2;
  }

  if (listingName) {
    score += 1;
  }

  return score;
};

export const isSelfAuthoredMessageNotification = notification => {
  const actorUserId = normalizeNotificationUserId(
    notification?.actorUserId || notification?.lastMessageUserId
  );
  const recipientUserId = normalizeNotificationUserId(notification?.recipientUserId);

  return !!actorUserId && !!recipientUserId && actorUserId === recipientUserId;
};

export const createMessageNotificationMap = (notifications = []) => {
  return notifications.reduce((acc, notification) => {
    const txId = getTxId(notification?.txId);

    if (!txId || isSelfAuthoredMessageNotification(notification)) {
      return acc;
    }

    acc[txId] = notification;
    return acc;
  }, {});
};

export const createNormalizedMessageNotificationMap = ({
  notifications = [],
  unreadMessageNotifications = [],
}) => {
  const unreadNotificationsByTxId = createMessageNotificationMap(unreadMessageNotifications);
  const normalizedNotifications = notifications.reduce((acc, notification) => {
    const txId = getTxId(notification?.txId);

    if (!txId) {
      return acc;
    }

    const unreadNotification = unreadNotificationsByTxId[txId];
    const unreadCount = isSelfAuthoredMessageNotification(notification)
      ? 0
      : Number(unreadNotification?.unreadCount || 0);

    acc[txId] = {
      ...notification,
      hasUnread: unreadCount > 0,
      unreadCount,
    };

    return acc;
  }, {});

  return unreadMessageNotifications.reduce((acc, unreadNotification) => {
    const txId = getTxId(unreadNotification?.txId);

    if (!txId || acc[txId] || isSelfAuthoredMessageNotification(unreadNotification)) {
      return acc;
    }

    const unreadCount = Number(unreadNotification?.unreadCount || 0);
    acc[txId] = {
      ...unreadNotification,
      hasUnread: unreadCount > 0,
      unreadCount,
    };

    return acc;
  }, normalizedNotifications);
};

export const getUnreadMessageNotificationTxIds = (notifications = []) => {
  return notifications
    .filter(
      notification =>
        Number(notification?.unreadCount || 0) > 0 &&
        getTxId(notification?.txId) &&
        !isSelfAuthoredMessageNotification(notification)
    )
    .map(notification => getTxId(notification.txId));
};

export const getCombinedNotificationCount = ({
  attentionTransactions = [],
  unreadMessageNotifications = [],
}) => {
  const attentionTxIds = attentionTransactions.map(tx => getTxId(tx)).filter(Boolean);
  const unreadMessageTxIds = unreadMessageNotifications
    .filter(notification => !isSelfAuthoredMessageNotification(notification))
    .map(notification => getTxId(notification?.txId))
    .filter(Boolean);

  return new Set([...attentionTxIds, ...unreadMessageTxIds]).size;
};

export const getStreamUnreadNotificationCount = (unreadMessageNotifications = []) => {
  return new Set(
    unreadMessageNotifications
      .filter(notification => !isSelfAuthoredMessageNotification(notification))
      .map(notification => getTxId(notification?.txId))
      .filter(Boolean)
  ).size;
};

export const getNotificationTargetTxId = notification => {
  return getTxId(notification?.targetId || notification?.txId);
};

export const getNotificationTimestamp = notification => {
  const value = notification?.occurredAt || notification?.lastMessageAt || null;
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const TRANSITION_DEDUPE_KEY_REGEX = /^tx:[^:]+:(transition\/[^:]+)(?::|$)/;
const CONFIRMED_BOOKING_DUPLICATE_WINDOW_MS = 5 * 60 * 1000;
const INQUIRY_MESSAGE_DUPLICATE_WINDOW_MS = 2 * 60 * 1000;
const INQUIRY_NOTIFICATION_DUPLICATE_WINDOW_MS = 5 * 60 * 1000;
const COMPLETED_BOOKING_DEDUPE_TRANSITION = 'transition/complete';
const COMPLETED_BOOKING_TRANSITIONS = new Set([
  COMPLETED_BOOKING_DEDUPE_TRANSITION,
  'transition/operator-complete',
  'transition/auto-complete-after-reschedule-booking',
  'transition/operator-complete-after-reschedule-booking',
]);

export const isStreamMessageNotification = notification => {
  return (
    notification?.source === 'stream-chat' ||
    String(notification?.$id || '').startsWith(STREAM_MESSAGE_NOTIFICATION_ID_PREFIX)
  );
};

export const isUnhydratedStreamMessageNotification = notification => {
  return (
    isStreamMessageNotification(notification) && getMessageNotificationHydrationScore(notification) === 0
  );
};

export const createStreamMessageNotificationItem = notification => {
  const txId = getTxId(notification?.txId);

  if (!txId) {
    return null;
  }

  const unreadCount = Number(notification?.unreadCount || 0);
  const hasUnread = unreadCount > 0;
  const lastMessageText = String(notification?.lastMessageText || '').trim();
  const targetUrl = String(notification?.targetUrl || '').trim();
  const actorName = String(notification?.actorName || '').trim();
  const actorUserId = notification?.actorUserId || notification?.lastMessageUserId || null;
  const recipientUserId = notification?.recipientUserId || null;

  if (
    isSelfAuthoredMessageNotification({
      ...notification,
      actorUserId,
      recipientUserId,
    })
  ) {
    return null;
  }

  const headline = actorName
    ? `You have a new message from ${actorName}.`
    : STREAM_MESSAGE_FALLBACK_HEADLINE;

  return {
    $id: `${STREAM_MESSAGE_NOTIFICATION_ID_PREFIX}${txId}`,
    channelCid: notification?.channelCid || null,
    channelId: notification?.channelId || `tx-${txId}`,
    headline,
    actorName: actorName || null,
    actorUserId,
    isRead: !hasUnread,
    lastMessageAt: notification?.lastMessageAt || null,
    lastMessageId: notification?.lastMessageId || null,
    lastMessageText,
    lastMessageUserId: notification?.lastMessageUserId || null,
    occurredAt: notification?.lastMessageAt || null,
    readAt: hasUnread ? null : notification?.lastMessageAt || null,
    recipientUserId,
    snippet: lastMessageText || STREAM_MESSAGE_FALLBACK_SNIPPET,
    source: 'stream-chat',
    targetId: txId,
    targetUrl: targetUrl || null,
    txId,
    type: 'message.new',
    unreadCount,
  };
};

const createStreamMessageNotificationMapByTxId = (streamItems = []) => {
  return streamItems.reduce((acc, item) => {
    const txId = getNotificationTargetTxId(item);

    if (!txId) {
      return acc;
    }

    acc[txId] = item;
    return acc;
  }, {});
};

const shouldPreferCentralizedMessageNotification = ({ notification, streamItem }) => {
  if (!streamItem) {
    return true;
  }

  const notificationTimestamp = getNotificationTimestamp(notification);
  const streamTimestamp = getNotificationTimestamp(streamItem);

  if (notificationTimestamp !== streamTimestamp) {
    return notificationTimestamp > streamTimestamp;
  }

  const notificationUnreadPriority = Number(!notification?.isRead);
  const streamUnreadPriority = Number(!streamItem?.isRead);

  if (notificationUnreadPriority !== streamUnreadPriority) {
    return notificationUnreadPriority > streamUnreadPriority;
  }

  const notificationHydrationScore = getMessageNotificationHydrationScore(notification);
  const streamHydrationScore = getMessageNotificationHydrationScore(streamItem);

  if (notificationHydrationScore !== streamHydrationScore) {
    return notificationHydrationScore > streamHydrationScore;
  }

  return false;
};

const getNotificationTransitionName = notification => {
  const dedupeKey = String(notification?.dedupeKey || '').trim();
  const match = dedupeKey.match(TRANSITION_DEDUPE_KEY_REGEX);
  return match?.[1] || null;
};

const normalizeNotificationText = value => {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

const parseMaybeJsonObject = value => {
  if (typeof value !== 'string') {
    return null;
  }

  const text = value.trim();
  if (!text.startsWith('{') || !text.endsWith('}')) {
    return null;
  }

  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

const normalizeObjectKeys = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return Object.entries(value).reduce((acc, [key, item]) => {
    return {
      ...acc,
      [String(key).toLowerCase()]: item,
    };
  }, {});
};

const getNotificationDeduplicationTitle = notification => {
  const directTitle = normalizeNotificationText(notification?.headline || notification?.title);
  if (directTitle) {
    return directTitle;
  }

  const localizedTitle =
    notification?.title_i18n || notification?.titleI18n || notification?.headlineI18n || null;
  const parsedLocalizedTitle =
    localizedTitle && typeof localizedTitle === 'object'
      ? localizedTitle
      : parseMaybeJsonObject(localizedTitle);
  const normalizedLocalizedTitle = normalizeObjectKeys(parsedLocalizedTitle);

  if (!normalizedLocalizedTitle) {
    return '';
  }

  const localizedValues = [normalizedLocalizedTitle.en, ...Object.values(normalizedLocalizedTitle)]
    .map(value => normalizeNotificationText(value))
    .filter(Boolean);

  return localizedValues[0] || '';
};

const getNotificationDuplicateKey = notification => {
  const title = getNotificationDeduplicationTitle(notification);
  const txId = getNotificationTargetTxId(notification);
  const timestamp = getNotificationTimestamp(notification);

  if (!title || !txId || !timestamp) {
    return null;
  }

  return [title, txId, timestamp].join('::');
};

const mergeDuplicateNotification = ({ current, candidate }) => {
  const anyRead = Boolean(current?.isRead) || Boolean(candidate?.isRead);
  const shouldTrackUnreadCount =
    'unreadCount' in (current || {}) ||
    'unreadCount' in (candidate || {}) ||
    'hasUnread' in (current || {}) ||
    'hasUnread' in (candidate || {});
  const unreadCount = anyRead
    ? 0
    : Math.max(Number(current?.unreadCount || 0), Number(candidate?.unreadCount || 0));
  const merged = {
    ...candidate,
    ...current,
    isRead: anyRead,
    readAt: anyRead ? current?.readAt || candidate?.readAt || null : null,
  };

  if (shouldTrackUnreadCount) {
    merged.unreadCount = unreadCount;
    merged.hasUnread = unreadCount > 0;
  }

  return merged;
};

export const dedupeNotifications = (notifications = []) => {
  const list = Array.isArray(notifications) ? notifications : [];
  const deduped = [];
  const indexesByKey = new Map();

  list.forEach(notification => {
    const duplicateKey = getNotificationDuplicateKey(notification);
    if (!duplicateKey) {
      deduped.push(notification);
      return;
    }

    if (!indexesByKey.has(duplicateKey)) {
      indexesByKey.set(duplicateKey, deduped.length);
      deduped.push(notification);
      return;
    }

    const existingIndex = indexesByKey.get(duplicateKey);
    deduped[existingIndex] = mergeDuplicateNotification({
      current: deduped[existingIndex],
      candidate: notification,
    });
  });

  return deduped;
};

const isCustomerPaymentSuccessNotification = notification => {
  if (notification?.type !== 'booking.updated') {
    return false;
  }

  const targetUrl = String(notification?.targetUrl || '').trim();
  if (targetUrl.startsWith('/sale/')) {
    return false;
  }

  return String(getNotificationTransitionName(notification) || '')
    .toLowerCase()
    .includes('confirm-payment');
};

const isPaymentSuccessNotification = notification => {
  if (notification?.type !== 'booking.updated') {
    return false;
  }

  return String(getNotificationTransitionName(notification) || '')
    .toLowerCase()
    .includes('confirm-payment');
};

const isInquiryTransitionNotification = notification => {
  if (notification?.type !== 'booking.requested') {
    return false;
  }

  return String(getNotificationTransitionName(notification) || '')
    .toLowerCase()
    .includes('transition/inquire');
};

const isConfirmedBookingNotification = notification => {
  return (
    notification?.type === 'booking.accepted' ||
    notification?.type === 'booking.secured' ||
    isCustomerPaymentSuccessNotification(notification)
  );
};

const isBookingRequestPaymentNotification = notification => {
  if (notification?.type !== 'booking.requested') {
    return false;
  }

  return String(getNotificationTransitionName(notification) || '')
    .toLowerCase()
    .includes('request-payment');
};

const isBookingRequestSupersedingNotification = notification => {
  return (
    notification?.type === 'booking.accepted' ||
    notification?.type === 'booking.secured' ||
    isPaymentSuccessNotification(notification)
  );
};

const isCompletedBookingNotification = notification => {
  if (notification?.type !== 'booking.updated') {
    return false;
  }

  return COMPLETED_BOOKING_TRANSITIONS.has(
    String(getNotificationTransitionName(notification) || '').toLowerCase()
  );
};

const getInquiryNotificationKey = notification => {
  if (!isInquiryTransitionNotification(notification)) {
    return null;
  }

  const recipientKey = String(notification?.recipientUserId || notification?.targetUrl || '').trim();
  const actorKey = String(notification?.actorUserId || '').trim();
  const txId = getNotificationTargetTxId(notification);

  if (!recipientKey || !txId) {
    return null;
  }

  const listingId = String(notification?.listingId || '').trim();

  return ['inquiry', recipientKey, actorKey, txId, listingId].join(':');
};

const getConfirmedBookingNotificationKey = notification => {
  if (!isConfirmedBookingNotification(notification)) {
    return null;
  }

  const recipientKey = String(notification?.recipientUserId || notification?.targetUrl || '').trim();
  const txId = getNotificationTargetTxId(notification);

  if (!recipientKey || !txId) {
    return null;
  }

  const listingId = String(notification?.listingId || '').trim();
  const bookingStartAt = String(notification?.bookingStartAt || '').trim();
  const bookingEndAt = String(notification?.bookingEndAt || '').trim();

  return ['confirmed-booking', recipientKey, txId, listingId, bookingStartAt, bookingEndAt].join(':');
};

const getCompletedBookingNotificationKey = notification => {
  if (!isCompletedBookingNotification(notification)) {
    return null;
  }

  const recipientKey = String(notification?.recipientUserId || notification?.targetUrl || '').trim();
  const type = String(notification?.type || '').trim();
  const txId = getNotificationTargetTxId(notification);
  const bookingStartAt = String(notification?.bookingStartAt || '').trim();
  const bookingEndAt = String(notification?.bookingEndAt || '').trim();
  const bookingTimezone = String(notification?.bookingTimezone || '').trim();

  if (recipientKey && type && txId && (bookingStartAt || bookingEndAt)) {
    return [
      'completed-booking',
      recipientKey,
      txId,
      type,
      COMPLETED_BOOKING_DEDUPE_TRANSITION,
      bookingStartAt || 'start-unknown',
      bookingEndAt || 'end-unknown',
      bookingTimezone || 'tz-unknown',
    ].join(':');
  }

  const timestamp = getNotificationTimestamp(notification);
  const headline = normalizeNotificationText(notification?.headline);
  const snippet = normalizeNotificationText(notification?.snippet);

  if (!recipientKey || !type || !timestamp || (!headline && !snippet)) {
    return null;
  }

  return [
    'completed-booking-fallback',
    recipientKey,
    txId || 'tx-unknown',
    type,
    timestamp,
    headline,
    snippet,
  ].join(':');
};

const shouldReplaceConfirmedBookingDuplicate = ({ current, candidate }) => {
  const getTypePriority = notification => {
    if (notification?.type === 'booking.secured') {
      return 2;
    }

    if (notification?.type === 'booking.accepted') {
      return 1;
    }

    return 0;
  };
  const currentTypePriority = getTypePriority(current);
  const candidateTypePriority = getTypePriority(candidate);

  if (candidateTypePriority !== currentTypePriority) {
    return candidateTypePriority > currentTypePriority;
  }

  const currentUnreadPriority = Number(!current?.isRead);
  const candidateUnreadPriority = Number(!candidate?.isRead);

  if (candidateUnreadPriority !== currentUnreadPriority) {
    return candidateUnreadPriority > currentUnreadPriority;
  }

  const currentTimestamp = getNotificationTimestamp(current);
  const candidateTimestamp = getNotificationTimestamp(candidate);

  if (candidateTimestamp !== currentTimestamp) {
    return candidateTimestamp > currentTimestamp;
  }

  return String(candidate?.$id || '').localeCompare(String(current?.$id || '')) < 0;
};

const mergeConfirmedBookingDuplicate = ({ current, candidate }) => {
  const keepCandidate = shouldReplaceConfirmedBookingDuplicate({ current, candidate });
  const preferred = keepCandidate ? candidate : current;
  const secondary = keepCandidate ? current : candidate;
  const latest =
    getNotificationTimestamp(candidate) >= getNotificationTimestamp(current) ? candidate : current;
  const allRead = Boolean(current?.isRead) && Boolean(candidate?.isRead);

  return {
    ...preferred,
    occurredAt: latest?.occurredAt || preferred?.occurredAt || secondary?.occurredAt || null,
    isRead: allRead,
    readAt: allRead ? preferred?.readAt || latest?.readAt || secondary?.readAt || null : null,
  };
};

const getNotificationLifecycleKey = notification => {
  const recipientKey = String(notification?.recipientUserId || notification?.targetUrl || '').trim();
  const txId = getNotificationTargetTxId(notification);

  if (!recipientKey || !txId) {
    return null;
  }

  return ['booking-lifecycle', recipientKey, txId].join(':');
};

const removeSupersededBookingRequestNotifications = (notifications = []) => {
  const acceptedTimestampsByKey = new Map();

  notifications.forEach(notification => {
    if (!isBookingRequestSupersedingNotification(notification)) {
      return;
    }

    const key = getNotificationLifecycleKey(notification);
    if (!key) {
      return;
    }

    const timestamp = getNotificationTimestamp(notification);
    const currentTimestamp = acceptedTimestampsByKey.get(key) || 0;
    acceptedTimestampsByKey.set(key, Math.max(currentTimestamp, timestamp));
  });

  if (acceptedTimestampsByKey.size === 0) {
    return notifications;
  }

  return notifications.filter(notification => {
    if (!isBookingRequestPaymentNotification(notification)) {
      return true;
    }

    const key = getNotificationLifecycleKey(notification);
    if (!key || !acceptedTimestampsByKey.has(key)) {
      return true;
    }

    const requestTimestamp = getNotificationTimestamp(notification);
    const acceptedTimestamp = acceptedTimestampsByKey.get(key);

    return requestTimestamp > acceptedTimestamp;
  });
};

const shouldReplaceCompletedBookingDuplicate = ({ current, candidate }) => {
  const currentUnreadPriority = Number(!current?.isRead);
  const candidateUnreadPriority = Number(!candidate?.isRead);

  if (candidateUnreadPriority !== currentUnreadPriority) {
    return candidateUnreadPriority > currentUnreadPriority;
  }

  const currentTimestamp = getNotificationTimestamp(current);
  const candidateTimestamp = getNotificationTimestamp(candidate);

  if (candidateTimestamp !== currentTimestamp) {
    return candidateTimestamp > currentTimestamp;
  }

  return String(candidate?.$id || '').localeCompare(String(current?.$id || '')) < 0;
};

const mergeCompletedBookingDuplicate = ({ current, candidate }) => {
  const keepCandidate = shouldReplaceCompletedBookingDuplicate({ current, candidate });
  const preferred = keepCandidate ? candidate : current;
  const secondary = keepCandidate ? current : candidate;
  const latest =
    getNotificationTimestamp(candidate) >= getNotificationTimestamp(current) ? candidate : current;
  const allRead = Boolean(current?.isRead) && Boolean(candidate?.isRead);

  return {
    ...preferred,
    occurredAt: latest?.occurredAt || preferred?.occurredAt || secondary?.occurredAt || null,
    isRead: allRead,
    readAt: allRead ? preferred?.readAt || latest?.readAt || secondary?.readAt || null : null,
  };
};

const shouldReplaceInquiryNotificationDuplicate = ({ current, candidate }) => {
  const currentReadPriority = Number(Boolean(current?.isRead));
  const candidateReadPriority = Number(Boolean(candidate?.isRead));

  if (candidateReadPriority !== currentReadPriority) {
    return candidateReadPriority > currentReadPriority;
  }

  const currentTimestamp = getNotificationTimestamp(current);
  const candidateTimestamp = getNotificationTimestamp(candidate);

  if (candidateTimestamp !== currentTimestamp) {
    return candidateTimestamp > currentTimestamp;
  }

  return String(candidate?.$id || '').localeCompare(String(current?.$id || '')) < 0;
};

const mergeInquiryNotificationDuplicate = ({ current, candidate }) => {
  const keepCandidate = shouldReplaceInquiryNotificationDuplicate({ current, candidate });
  const preferred = keepCandidate ? candidate : current;
  const secondary = keepCandidate ? current : candidate;
  const latest =
    getNotificationTimestamp(candidate) >= getNotificationTimestamp(current) ? candidate : current;
  const anyRead = Boolean(current?.isRead) || Boolean(candidate?.isRead);

  return {
    ...preferred,
    occurredAt: latest?.occurredAt || preferred?.occurredAt || secondary?.occurredAt || null,
    isRead: anyRead,
    readAt: anyRead ? preferred?.readAt || secondary?.readAt || latest?.readAt || null : null,
  };
};

const dedupeInquiryNotifications = (notifications = []) => {
  const deduped = [];
  const groupsByKey = new Map();

  notifications.forEach(notification => {
    const dedupeKey = getInquiryNotificationKey(notification);
    if (!dedupeKey) {
      deduped.push(notification);
      return;
    }

    const timestamp = getNotificationTimestamp(notification);
    const groups = groupsByKey.get(dedupeKey) || [];
    const existingGroup = groups.find(
      group => Math.abs(group.latestTimestamp - timestamp) <= INQUIRY_NOTIFICATION_DUPLICATE_WINDOW_MS
    );

    if (!existingGroup) {
      const group = {
        index: deduped.length,
        latestTimestamp: timestamp,
        notification,
      };

      groups.push(group);
      groupsByKey.set(dedupeKey, groups);
      deduped.push(notification);
      return;
    }

    const mergedNotification = mergeInquiryNotificationDuplicate({
      current: existingGroup.notification,
      candidate: notification,
    });

    existingGroup.latestTimestamp = Math.max(existingGroup.latestTimestamp, timestamp);
    existingGroup.notification = mergedNotification;
    deduped[existingGroup.index] = mergedNotification;
  });

  return deduped;
};

const dedupeConfirmedBookingNotifications = (notifications = []) => {
  const deduped = [];
  const groupsByKey = new Map();

  notifications.forEach(notification => {
    const dedupeKey = getConfirmedBookingNotificationKey(notification);
    if (!dedupeKey) {
      deduped.push(notification);
      return;
    }

    const timestamp = getNotificationTimestamp(notification);
    const groups = groupsByKey.get(dedupeKey) || [];
    const existingGroup = groups.find(
      group => Math.abs(group.latestTimestamp - timestamp) <= CONFIRMED_BOOKING_DUPLICATE_WINDOW_MS
    );

    if (!existingGroup) {
      const group = {
        index: deduped.length,
        latestTimestamp: timestamp,
        notification,
      };

      groups.push(group);
      groupsByKey.set(dedupeKey, groups);
      deduped.push(notification);
      return;
    }

    const mergedNotification = mergeConfirmedBookingDuplicate({
      current: existingGroup.notification,
      candidate: notification,
    });

    existingGroup.latestTimestamp = Math.max(existingGroup.latestTimestamp, timestamp);
    existingGroup.notification = mergedNotification;
    deduped[existingGroup.index] = mergedNotification;
  });

  return deduped;
};

const dedupeCompletedBookingNotifications = (notifications = []) => {
  const deduped = [];
  const notificationsByKey = new Map();

  notifications.forEach(notification => {
    const dedupeKey = getCompletedBookingNotificationKey(notification);
    if (!dedupeKey) {
      deduped.push(notification);
      return;
    }

    const existing = notificationsByKey.get(dedupeKey);
    if (!existing) {
      notificationsByKey.set(dedupeKey, {
        index: deduped.length,
        notification,
      });
      deduped.push(notification);
      return;
    }

    const mergedNotification = mergeCompletedBookingDuplicate({
      current: existing.notification,
      candidate: notification,
    });

    existing.notification = mergedNotification;
    deduped[existing.index] = mergedNotification;
  });

  return deduped;
};

const createInquiryNotificationTimestampsByTxId = (notifications = []) => {
  return notifications.reduce((acc, notification) => {
    if (!isInquiryTransitionNotification(notification)) {
      return acc;
    }

    const txId = getNotificationTargetTxId(notification);
    if (!txId) {
      return acc;
    }

    const timestamp = getNotificationTimestamp(notification);
    if (!acc[txId]) {
      acc[txId] = [];
    }

    acc[txId].push(timestamp);
    return acc;
  }, {});
};

const isLegacyInquiryMessageDuplicate = ({ notification, inquiryNotificationTimestampsByTxId }) => {
  if (notification?.type !== 'message.new') {
    return false;
  }

  const txId = getNotificationTargetTxId(notification);
  if (!txId) {
    return false;
  }

  const inquiryTimestamps = inquiryNotificationTimestampsByTxId[txId] || [];
  if (!inquiryTimestamps.length) {
    return false;
  }

  const timestamp = getNotificationTimestamp(notification);

  return inquiryTimestamps.some(
    inquiryTimestamp => Math.abs(inquiryTimestamp - timestamp) <= INQUIRY_MESSAGE_DUPLICATE_WINDOW_MS
  );
};

export const mergeNotificationFeed = ({ notifications = [], messageNotifications = [] }) => {
  const streamItems = messageNotifications.map(createStreamMessageNotificationItem).filter(Boolean);
  const streamItemsByTxId = createStreamMessageNotificationMapByTxId(streamItems);
  const suppressedStreamTxIds = new Set();
  const exactDedupedNotifications = dedupeNotifications(notifications);
  const dedupedInquiryNotifications = dedupeInquiryNotifications(exactDedupedNotifications);
  const inquiryNotificationTimestampsByTxId = createInquiryNotificationTimestampsByTxId(
    dedupedInquiryNotifications
  );
  const mergedNotifications = dedupedInquiryNotifications.filter(notification => {
    if (notification?.type !== 'message.new') {
      return true;
    }

    if (isSelfAuthoredMessageNotification(notification)) {
      return false;
    }

    if (
      isLegacyInquiryMessageDuplicate({
        notification,
        inquiryNotificationTimestampsByTxId,
      })
    ) {
      return false;
    }

    const txId = getNotificationTargetTxId(notification);
    if (!txId) {
      return true;
    }

    const streamItem = streamItemsByTxId[txId];
    if (!streamItem) {
      return true;
    }

    if (shouldPreferCentralizedMessageNotification({ notification, streamItem })) {
      suppressedStreamTxIds.add(txId);
      return true;
    }

    return false;
  });
  const dedupedBookingNotifications = dedupeCompletedBookingNotifications(
    dedupeConfirmedBookingNotifications(mergedNotifications)
  );
  const consolidatedBookingNotifications = removeSupersededBookingRequestNotifications(
    dedupedBookingNotifications
  );
  const merged = dedupeNotifications([
    ...consolidatedBookingNotifications,
    ...streamItems.filter(item => {
      const txId = getNotificationTargetTxId(item);
      return !txId || !suppressedStreamTxIds.has(txId);
    }),
  ]);

  return merged.sort((a, b) => {
    const timestampDifference = getNotificationTimestamp(b) - getNotificationTimestamp(a);
    if (timestampDifference !== 0) {
      return timestampDifference;
    }

    const unreadDifference = Number(!b?.isRead) - Number(!a?.isRead);
    if (unreadDifference !== 0) {
      return unreadDifference;
    }

    return String(a?.$id || '').localeCompare(String(b?.$id || ''));
  });
};

export const getLatestActivityTimestamp = (transaction, messageNotification) => {
  const lastTransitionedAt = transaction?.attributes?.lastTransitionedAt;
  const lastMessageAt = messageNotification?.lastMessageAt;

  const transitionTimestamp = lastTransitionedAt ? new Date(lastTransitionedAt).getTime() : 0;
  const messageTimestamp = lastMessageAt ? new Date(lastMessageAt).getTime() : 0;

  return Math.max(transitionTimestamp, messageTimestamp, 0);
};

export const sortTransactionsByInboxActivity = (
  transactions = [],
  messageNotificationsByTxId = {},
  attentionTxIds = []
) => {
  const attentionTxIdSet = new Set(attentionTxIds.map(getTxId).filter(Boolean));

  return [...transactions].sort((transactionA, transactionB) => {
    const txIdA = getTxId(transactionA);
    const txIdB = getTxId(transactionB);
    const attentionPriorityA = attentionTxIdSet.has(txIdA) ? 1 : 0;
    const attentionPriorityB = attentionTxIdSet.has(txIdB) ? 1 : 0;

    if (attentionPriorityA !== attentionPriorityB) {
      return attentionPriorityB - attentionPriorityA;
    }

    const latestA = getLatestActivityTimestamp(transactionA, messageNotificationsByTxId[txIdA]);
    const latestB = getLatestActivityTimestamp(transactionB, messageNotificationsByTxId[txIdB]);

    return latestB - latestA;
  });
};

export const hasUnreadMessageNotification = (txId, messageNotificationsByTxId = {}) => {
  return Number(messageNotificationsByTxId?.[txId]?.unreadCount || 0) > 0;
};
